/**
 * Copyright (c) 2025 Stefan Radig
 *
 * PWA service worker registration and update handling.
 */

// Toast is handled inline for PWA updates

/**
 * Initialize PWA service worker registration.
 * Handles automatic updates and shows a toast when a new version is available.
 */
export async function initPWA(): Promise<void> {
    // Only register in production (service worker is not generated in dev mode)
    if (import.meta.env.DEV) {
        console.log('[PWA] Skipping service worker registration in development mode');
        return;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
        console.log('[PWA] Service workers not supported');
        return;
    }

    try {
        // Manually register with absolute path (required for MPA with nested HTML files)
        const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

        // Check for updates periodically
        registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // New version available
                        showUpdateToast(() => {
                            newWorker.postMessage({ type: 'SKIP_WAITING' });
                            window.location.reload();
                        });
                    }
                });
            }
        });

        // Listen for controller change (new SW took over)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            // New service worker activated
        });

    } catch (error) {
        console.error('[PWA] Service worker registration error:', error);
    }
}

/**
 * Show a toast notification for app updates.
 */
function showUpdateToast(onUpdate: () => void): void {
    // Create update toast with action button
    const toastContainer = document.createElement('div');
    toastContainer.id = 'pwa-update-toast';
    toastContainer.innerHTML = `
        <span>New version available!</span>
        <button id="pwa-update-btn">Update</button>
        <button id="pwa-dismiss-btn">Later</button>
    `;

    // Apply styles
    Object.assign(toastContainer.style, {
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#667eea',
        color: 'white',
        padding: '12px 20px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        zIndex: '10001',
        fontSize: '14px',
        fontWeight: '500'
    });

    document.body.appendChild(toastContainer);

    // Style buttons
    const buttons = toastContainer.querySelectorAll('button');
    buttons.forEach((btn, index) => {
        Object.assign(btn.style, {
            padding: '6px 12px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            background: index === 0 ? 'white' : 'rgba(255,255,255,0.2)',
            color: index === 0 ? '#667eea' : 'white'
        });
    });

    // Handle update button
    document.getElementById('pwa-update-btn')?.addEventListener('click', () => {
        toastContainer.remove();
        onUpdate();
    });

    // Handle dismiss button
    document.getElementById('pwa-dismiss-btn')?.addEventListener('click', () => {
        toastContainer.remove();
    });
}
